import { useMemo, useState } from "react";
import { TagGroup, TagList, type Key } from "react-aria-components";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import {
  useGetV4AdvReportByAd,
  useGetV4AdvReportByAdInfiniteQueryOptions,
  type AdvertisementQueryV4Row,
} from "../../modules/perf_reports/api/ads/v4AdvReportByAd";
import { css } from "../../../styled-system/css";
import { Combobox, ComboboxLoadMoreItem } from "../fields/base/Combobox";
import Tag from "../core/basic/Tag";

type AdOption = {
  label: string;
  value: number;
};

const MEDIA_PLAN_PAGE_SIZE = 50;

export function MediaPlanFilterSelect({
  value,
  onChange,
}: {
  value: (string | number)[];
  onChange: (values: (string | number)[]) => void;
}) {
  const { t } = useTranslation(["advertising"]);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);

  const selectedAdIds = useMemo(() => value.filter((v): v is number => typeof v === "number"), [value]);
  const adByAdOptionsGetter = useGetV4AdvReportByAd();
  const selectedAdsQuery = useQuery({
    ...adByAdOptionsGetter({
      pagination: { pageIndex: 0, pageSize: selectedAdIds.length || 1 },
      sorting: [],
      filters: [{ id: "ad_id", value: { op: "anyof", value: selectedAdIds } }],
    }),
    enabled: selectedAdIds.length > 0,
  });

  const advByAdQuery = useInfiniteQuery(
    useGetV4AdvReportByAdInfiniteQueryOptions({
      pagination: { pageIndex: 0, pageSize: MEDIA_PLAN_PAGE_SIZE },
      sorting: [{ id: "ad_name", desc: false }],
      filters: [],
      search: debouncedSearch,
    }),
  );

  const options: AdOption[] = useMemo(() => {
    const pages = advByAdQuery.data?.pages ?? [];
    const ads = pages.flatMap((page) => page.data);
    const selectedSet = new Set(value);
    return ads.filter((ad) => !selectedSet.has(ad.ad_id)).map((ad) => ({ value: ad.ad_id, label: ad.ad_name }));
  }, [advByAdQuery.data?.pages, value]);

  const selectedTags = useMemo(() => {
    const fetchedAds = new Map<number, string>(
      (selectedAdsQuery.data?.data ?? []).map((ad: AdvertisementQueryV4Row) => [ad.ad_id, ad.ad_name]),
    );
    return selectedAdIds.map((id) => ({
      id: String(id),
      label: fetchedAds.get(id) ?? t(($) => $.advertising.mediaPlanFilter["Ad #{id}"], { id }),
    }));
  }, [selectedAdIds, selectedAdsQuery.data, t]);

  const handleRemove = (tagId: string) => {
    const adId = Number(tagId);
    const next = value.filter((v) => v !== adId);
    onChange(next.length === 0 ? ["all"] : next);
  };

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "small" })}>
      {selectedTags.length > 0 && (
        <TagGroup aria-label={t(($) => $.advertising.mediaPlanFilter["Selected ads"])}>
          <TagList
            items={selectedTags}
            className={css({
              display: "flex",
              gap: "small",
              flexWrap: "wrap",
              maxHeight: "110px",
              overflowY: "auto",
            })}
          >
            {(tag: { id: string; label: string }) => <Tag tag={tag} onRemove={handleRemove} />}
          </TagList>
        </TagGroup>
      )}
      <Combobox
        aria-label={t(($) => $.advertising.mediaPlanFilter["Search media plans"])}
        items={options}
        inputValue={searchInput}
        onInputChange={setSearchInput}
        value={null}
        onChange={(key: Key | null) => {
          if (key == null) return;
          const adId = Number(key);
          const next = value.filter((v) => v !== "all");
          if (!next.includes(adId)) {
            next.push(adId);
          }
          onChange(next);
        }}
        defaultFilter={() => true}
        isLoading={advByAdQuery.isFetching && !advByAdQuery.isFetchingNextPage}
        placeholder={t(($) => $.advertising.mediaPlanFilter["Search ads..."])}
        listBoxFooter={
          advByAdQuery.hasNextPage ?
            <ComboboxLoadMoreItem
              isLoading={advByAdQuery.isFetchingNextPage}
              onLoadMore={() => {
                if (!advByAdQuery.isFetchingNextPage) {
                  void advByAdQuery.fetchNextPage();
                }
              }}
            />
          : undefined
        }
      />
    </div>
  );
}
